/**
 * Recover from "Failed to fetch dynamically imported module".
 *
 * THE FAILURE. Every route in this app is `React.lazy(() => import(...))`, so
 * the page you are looking at is one `index.html` plus a set of
 * content-hashed chunks (`CoportalWelcomeMeetingsPage-CTFJoZxT.js`). A deploy
 * rebuilds those chunks with NEW hashes and replaces the web container
 * wholesale, so the previous build's files stop existing — they 404, they are
 * not merely stale. A browser tab that was already open is still running the
 * OLD `index.html`, which knows only the OLD names. The moment the operator
 * navigates to a route they had not visited yet, the import 404s and React
 * throws
 *
 *     error loading dynamically imported module: .../CoportalWelcomeMeetingsPage-CTFJoZxT.js
 *
 * A manual refresh fixes it because that fetches the new `index.html`, which
 * points at names that exist. That is exactly what this does automatically.
 *
 * WHY THIS IS THE RIGHT LAYER. The alternative is retaining old assets on the
 * server so the old names keep resolving, which means an assets volume, a
 * retention policy and a way to decide when a chunk is finally dead. That is
 * worth doing eventually, but it only shrinks the window — the tab is still
 * running superseded code. Reloading is what the operator wants either way.
 *
 * SAFETY. A reload loop is far worse than the error it replaces, so:
 *   · at most ONE automatic reload per `RELOAD_WINDOW_MS`, tracked in
 *     sessionStorage (per tab, cleared when the tab closes)
 *   · if a reload has already been tried inside that window, the error is
 *     left to propagate so the error boundary can show it — a genuinely
 *     broken build must stay visible rather than reloading forever
 */

const RELOAD_KEY = 'amassa:chunk-reload-at';
const RELOAD_WINDOW_MS = 15_000;

/** Message shapes browsers use for a failed dynamic import. */
const CHUNK_ERROR = /(dynamically imported module|Importing a module script failed|error loading dynamically|Failed to fetch dynamically)/i;

function reloadedRecently(): boolean {
  try {
    const raw = sessionStorage.getItem(RELOAD_KEY);
    if (!raw) return false;
    const at = Number(raw);
    return Number.isFinite(at) && Date.now() - at < RELOAD_WINDOW_MS;
  } catch {
    // Private mode / storage disabled: fail CLOSED (assume we already
    // reloaded) so a storage error can never produce a reload loop.
    return true;
  }
}

function markReloaded(): void {
  try {
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    /* storage unavailable — the guard above already fails closed */
  }
}

/** Reload once; returns false when the guard blocks it. */
function reloadOnce(reason: string): boolean {
  if (reloadedRecently()) return false;
  markReloaded();
  // eslint-disable-next-line no-console
  console.warn(`[amassa] stale chunk (${reason}) — reloading to pick up the current build`);
  window.location.reload();
  return true;
}

export function installStaleChunkReload(): void {
  // Vite's own signal, raised by the preload helper that wraps every dynamic
  // import in the production build. This is the one that fires first and
  // catches the common case.
  window.addEventListener('vite:preloadError', (event) => {
    // preventDefault stops Vite rethrowing, so the blank-screen error
    // boundary never renders for a case we are about to fix.
    if (reloadOnce('vite:preloadError')) event.preventDefault();
  });

  // Belt and braces: a rejected import that did NOT come through the preload
  // helper (a manual `import()` in a component, a retry after hydration)
  // surfaces here instead.
  window.addEventListener('unhandledrejection', (event) => {
    const msg = String(
      (event.reason as { message?: string } | undefined)?.message ?? event.reason ?? '',
    );
    if (CHUNK_ERROR.test(msg) && reloadOnce('unhandledrejection')) {
      event.preventDefault();
    }
  });
}
